import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { Plus, Edit, Trash2, Search, Eye, EyeOff, Globe, Link as LinkIcon, AlertCircle, Upload, Download, X } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPublications = () => {
  const navigate = useNavigate();
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkData, setBulkData] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || user.role !== 'admin') {
      toast.error('Admin access required');
      navigate('/admin/login');
      return;
    }
    
    fetchPublications();
  }, [searchQuery, categoryFilter]);

  const fetchPublications = async () => {
    const token = localStorage.getItem('token');
    
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (categoryFilter) params.append('category', categoryFilter);
      
      const response = await axios.get(`${API}/admin/publications?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const publicationsData = response.data || [];
      setPublications(publicationsData);
      setTotal(publicationsData.length);
    } catch (error) {
      console.error('Error fetching publications:', error);
      toast.error('Failed to load publications');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This will also delete all associated packages.`)) {
      return;
    }

    const token = localStorage.getItem('token');
    
    try {
      await axios.delete(`${API}/admin/publications/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Publication deleted successfully');
      fetchPublications();
    } catch (error) {
      console.error('Error deleting publication:', error);
      toast.error('Failed to delete publication');
    }
  };

  const downloadTemplate = () => {
    const template = 'name,categories,description,language,da,disclaimer,backlink,is_featured,tagline,restrictions\nThe Economic Times,"Business,Finance",Leading business newspaper,English,85,No,Yes,Yes,Your trusted business partner,No competitor mentions\nYourStory,"Startup,Technology",Startup focused publication,English,72,Yes,Yes,No,Stories that inspire,';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'publications_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) return [];
    
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
    const data = [];
    
    for (let i = 1; i < lines.length; i++) {
      const values = [];
      let current = '';
      let inQuotes = false;
      
      for (const char of lines[i]) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());
      
      const row = {};
      headers.forEach((header, idx) => {
        row[header] = values[idx] || '';
      });
      if (row.name) data.push(row);
    }
    return data;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const data = parseCSV(text);
      setBulkData(data);
      setShowBulkUpload(true);
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleBulkUpload = async () => {
    if (bulkData.length === 0) return;
    
    setUploading(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post(`${API}/admin/publications/bulk-upload`, bulkData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Created ${response.data.created} publications`);
      if (response.data.errors.length > 0) {
        toast.error(`${response.data.errors.length} rows had errors`);
      }
      
      setShowBulkUpload(false);
      setBulkData([]);
      fetchPublications();
    } catch (error) {
      console.error('Bulk upload error:', error);
      toast.error('Bulk upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="font-playfair text-4xl font-bold mb-2">Publications</h1>
          <p className="font-manrope text-sm text-gray-600">Manage media outlets and publications</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={downloadTemplate}
            className="flex items-center gap-2 px-4 py-3 border border-gray-300 text-gray-700 font-manrope text-xs uppercase tracking-widest hover:bg-gray-50 transition-all duration-300"
          >
            <Download className="w-4 h-4" />
            Template
          </button>
          <label className="flex items-center gap-2 px-4 py-3 border border-[#D4AF37] text-[#D4AF37] font-manrope text-xs uppercase tracking-widest hover:bg-[#D4AF37] hover:text-white transition-all duration-300 cursor-pointer">
            <Upload className="w-4 h-4" />
            Bulk Upload
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              ref={fileInputRef}
              className="hidden"
            />
          </label>
          <Link
            to="/admin/publications/create"
            data-testid="create-publication-button"
            className="flex items-center gap-2 px-6 py-3 bg-black text-white font-manrope text-xs uppercase tracking-widest hover:bg-gray-800 transition-all duration-300"
          >
            <Plus className="w-4 h-4" />
            Add Publication
          </Link>
        </div>
      </div>

      {/* Bulk Upload Modal */}
      {showBulkUpload && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="font-playfair text-2xl font-bold">Preview Bulk Upload</h2>
              <button onClick={() => { setShowBulkUpload(false); setBulkData([]); }}>
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-6">
              <p className="font-manrope text-sm text-gray-600 mb-4">{bulkData.length} publications to import</p>
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left p-2">Name</th>
                    <th className="text-left p-2">Categories</th>
                    <th className="text-left p-2">Language</th>
                    <th className="text-left p-2">DA</th>
                    <th className="text-left p-2">Featured</th>
                  </tr>
                </thead>
                <tbody>
                  {bulkData.map((row, idx) => (
                    <tr key={idx} className="border-b">
                      <td className="p-2 font-semibold">{row.name}</td>
                      <td className="p-2">{row.categories}</td>
                      <td className="p-2">{row.language || 'English'}</td>
                      <td className="p-2">{row.da || '-'}</td>
                      <td className="p-2">{row.is_featured?.toLowerCase() === 'yes' ? '✓' : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => { setShowBulkUpload(false); setBulkData([]); }}
                className="px-6 py-2 border border-gray-300 font-manrope text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkUpload}
                disabled={uploading}
                className="px-6 py-2 bg-[#D4AF37] text-white font-manrope text-sm hover:bg-[#B8960F] disabled:opacity-50"
              >
                {uploading ? 'Importing...' : `Import ${bulkData.length} Publications`}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 border border-gray-200 p-6 bg-white">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Search</label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name or description..."
                data-testid="search-publications-input"
                className="w-full border border-gray-200 pl-10 pr-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
              />
              <Search className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
            </div>
          </div>
          
          <div>
            <label className="font-manrope text-xs text-gray-600 mb-2 block uppercase tracking-widest">Category</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              data-testid="category-filter-select"
              className="w-full border border-gray-200 px-4 py-2 font-manrope text-sm focus:ring-1 focus:ring-black focus:outline-none"
            >
              <option value="">All Categories</option>
              <option value="Business">Business</option>
              <option value="Startup">Startup</option>
              <option value="Tech">Tech</option>
              <option value="Real Estate">Real Estate</option>
              <option value="Healthcare">Healthcare</option>
              <option value="Lifestyle">Lifestyle</option>
              <option value="Entertainment">Entertainment</option>
              <option value="Finance">Finance</option>
              <option value="Education">Education</option>
              <option value="Sports">Sports</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <p className="font-manrope text-gray-600">Loading...</p>
        </div>
      ) : publications.length === 0 ? (
        <div className="text-center py-12 border border-gray-200 p-12">
          <p className="font-manrope text-gray-600 mb-4">No publications found</p>
          <Link
            to="/admin/publications/create"
            className="inline-block px-6 py-3 bg-black text-white font-manrope text-xs uppercase tracking-widest hover:bg-gray-800 transition-all duration-300"
          >
            Create First Publication
          </Link>
        </div>
      ) : (
        <>
          <div className="mb-4">
            <p className="font-manrope text-sm text-gray-600">Total: {total} publications</p>
          </div>
          
          <div className="border border-gray-200 overflow-x-auto">
            <table className="w-full min-w-[1000px]">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Publication</th>
                  <th className="text-left px-4 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Category</th>
                  <th className="text-left px-4 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Language</th>
                  <th className="text-center px-4 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">DA</th>
                  <th className="text-left px-4 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Img Res.</th>
                  <th className="text-center px-4 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Disclaimer</th>
                  <th className="text-center px-4 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Backlink</th>
                  <th className="text-left px-4 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Status</th>
                  <th className="text-right px-4 py-3 font-manrope text-xs uppercase tracking-widest text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {publications.map((pub) => (
                  <tr key={pub.id} className="border-b border-gray-200 hover:bg-gray-50" data-testid={`publication-row-${pub.id}`}>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-100 flex items-center justify-center flex-shrink-0">
                          {pub.logo ? (
                            <img src={pub.logo} alt={pub.name} className="w-full h-full object-contain" />
                          ) : (
                            <span className="font-mono text-xs text-gray-400">N/A</span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-manrope font-semibold text-sm truncate">{pub.name}</div>
                          {pub.is_featured && (
                            <span className="inline-block px-1.5 py-0.5 bg-black text-white font-manrope text-[10px] uppercase">
                              Featured
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-1">
                        {(pub.categories || [pub.category]).filter(Boolean).map((cat, idx) => (
                          <span key={idx} className="inline-block px-2 py-1 bg-gray-100 font-manrope text-xs uppercase tracking-widest">
                            {cat}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1.5">
                        <Globe className="w-3 h-3 text-gray-400" />
                        <span className="font-manrope text-sm">{pub.language || 'English'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {pub.da ? (
                        <span className={`inline-block px-2 py-1 font-mono text-xs font-bold ${
                          pub.da >= 70 ? 'bg-green-100 text-green-800' :
                          pub.da >= 40 ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {pub.da}
                        </span>
                      ) : (
                        <span className="font-manrope text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <span className="font-manrope text-xs text-gray-600">{pub.image_resolution || '-'}</span>
                    </td>
                    <td className="px-4 py-4 text-center">
                      {pub.disclaimer ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs font-manrope rounded">
                          <AlertCircle className="w-3 h-3" /> Yes
                        </span>
                      ) : (
                        <span className="font-manrope text-xs text-gray-400">No</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-center">
                      {pub.backlink !== false ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-manrope rounded">
                          <LinkIcon className="w-3 h-3" /> Yes
                        </span>
                      ) : (
                        <span className="font-manrope text-xs text-gray-400">No</span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-1.5">
                        {pub.is_active ? (
                          <>
                            <Eye className="w-3 h-3 text-green-600" />
                            <span className="font-manrope text-xs text-green-600">Active</span>
                          </>
                        ) : (
                          <>
                            <EyeOff className="w-3 h-3 text-gray-400" />
                            <span className="font-manrope text-xs text-gray-400">Inactive</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/admin/publications/edit/${pub.id}`}
                          data-testid={`edit-publication-${pub.id}`}
                          className="p-2 border border-gray-200 hover:border-black transition-all duration-300"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => handleDelete(pub.id, pub.name)}
                          data-testid={`delete-publication-${pub.id}`}
                          className="p-2 border border-gray-200 hover:border-red-600 hover:text-red-600 transition-all duration-300"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminPublications;